import { useState } from 'react';
import type { CheckerView } from '../api/uptime';

const DAY_MS = 86_400_000;

export function dayIncidents(checker: CheckerView, target: string, date: string, now: number) {
  const start = Date.parse(`${date}T00:00:00Z`);
  const end = start + DAY_MS;
  const observedUntil = Math.min(now, Date.parse(checker.report?.generatedAt ?? ''), end);
  return (checker.report?.incidents ?? [])
    .filter(incident => incident.target === target)
    .map(incident => ({
      ...incident,
      start: Math.max(start, Date.parse(incident.startedAt)),
      end: Math.min(observedUntil, Date.parse(incident.endedAt ?? '') || observedUntil),
    }))
    .filter(incident => incident.end > incident.start)
    .sort((a, b) => a.start - b.start);
}

function clock(time: number, dayEnd: number) {
  return time === dayEnd ? '24:00' : new Date(time).toISOString().slice(11, 16);
}

export function UptimeDayDetail({
  checkers,
  target,
  now,
}: {
  checkers: CheckerView[];
  target: string;
  now: number;
}) {
  const today = new Date(now).toISOString().slice(0, 10);
  const earliest = new Date(now - 89 * DAY_MS).toISOString().slice(0, 10);
  const [chosen, setChosen] = useState(today);
  const date = chosen < earliest || chosen > today ? today : chosen;
  const start = Date.parse(`${date}T00:00:00Z`);
  const end = start + DAY_MS;
  return (
    <div className="min-w-80 space-y-3 py-2">
      <label className="flex items-center gap-2 text-sm">
        Day (UTC)
        <input
          type="date"
          min={earliest}
          max={today}
          value={date}
          onChange={event => {
            if (event.target.value) setChosen(event.target.value);
          }}
          className="rounded-3xl border bg-background px-3 py-2"
        />
      </label>
      <p className="max-w-lg text-xs text-muted-foreground">
        Recorded outages in red. Checks run every 5 minutes, so times reflect checks. Unmarked time
        does not confirm availability.
      </p>
      {checkers.map(checker => {
        const incidents = dayIncidents(checker, target, date, now);
        const hasChecks = checker.report?.days.some(
          day => day.target === target && day.date === date && day.checks > 0
        );
        return (
          <div key={checker.id} className="space-y-1 text-xs">
            <div className="font-medium">From {checker.label}</div>
            <div
              role="img"
              aria-label={`${checker.label} outages on ${date}, UTC`}
              className="relative h-6 overflow-hidden rounded-sm bg-muted"
            >
              {Array.from({ length: 23 }, (_, index) => (
                <span
                  key={index}
                  className="absolute inset-y-0 border-l border-background"
                  style={{ left: `${((index + 1) / 24) * 100}%` }}
                />
              ))}
              {incidents.map(incident => (
                <span
                  key={incident.startedAt}
                  title={`${clock(incident.start, end)} to ${clock(incident.end, end)} UTC`}
                  className="absolute inset-y-0 min-w-px bg-destructive"
                  style={{
                    left: `${((incident.start - start) / DAY_MS) * 100}%`,
                    width: `${((incident.end - incident.start) / DAY_MS) * 100}%`,
                  }}
                />
              ))}
            </div>
            <div className="flex justify-between tabular-nums text-muted-foreground">
              {['00:00', '06:00', '12:00', '18:00', '24:00'].map(hour => (
                <span key={hour}>{hour}</span>
              ))}
            </div>
            {incidents.length ? (
              <ul className="space-y-1 pt-1">
                {incidents.map(incident => (
                  <li key={incident.startedAt}>
                    {clock(incident.start, end)} to {clock(incident.end, end)} UTC
                    {!incident.endedAt && ' · Still down at last report'}
                    <div className="max-w-lg whitespace-normal text-muted-foreground">
                      {incident.error}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">
                {hasChecks ? 'No recorded outages on this day.' : 'No checks on this day.'}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
